/**
 * DecisionNode - Conditional branch node (card style)
 */
import { useContext } from 'react';
import { Handle, Position } from 'reactflow';
import { IonIcon } from '@ionic/react';
import { gitBranchOutline } from 'ionicons/icons';
import { WorkflowActionsContext } from '../contexts/WorkflowActionsContext';
// ONE declaration of the anonymous-fork caption (graphs.js decisionNodeLabel
// normally supplies it; this default is the last resort). Importing it instead
// of repeating the string keeps the two from drifting apart.
import { BRANCH_NODE_LABEL } from '../constants/graphs';
import { RunStack, runningClass } from './RunStack';
import './DecisionNode.css';

export const DecisionNode = ({ data, isConnectable }) => {
  const {
    label = BRANCH_NODE_LABEL, onClick, nodeId, canDelete = false,
    description = null, descriptionFull = null,
  } = data || {};
  // A router IS a step — the engine reports it like any other node — so it
  // lights like any other node. See RunStack.
  const activeRuns = Math.max(0, Number(data?.activeRuns) || 0);
  const actions = useContext(WorkflowActionsContext);
  
  const handleContextMenu = (e) => {
    e.preventDefault();
    e.stopPropagation();
    actions?.onNodeContextMenu(nodeId, { x: e.clientX, y: e.clientY });
  };
  
  const handles = (
    <>
      <Handle type="target" position={Position.Top} id="top" isConnectable={isConnectable} className="gwe-decision-node__handle" />
      <Handle type="target" position={Position.Left} id="target-left" isConnectable={isConnectable} className="gwe-decision-node__handle gwe-decision-node__handle--side" />
      <Handle type="target" position={Position.Right} id="target-right" isConnectable={isConnectable} className="gwe-decision-node__handle gwe-decision-node__handle--side" />
      {/* Bottom TARGET handle — loop-back landing point (see WorkflowNode). */}
      <Handle type="target" position={Position.Bottom} id="target-bottom" isConnectable={isConnectable} className="gwe-decision-node__handle" />
      <Handle type="source" position={Position.Top} id="source-top" isConnectable={false} className="gwe-decision-node__handle" />
      <Handle type="source" position={Position.Bottom} id="bottom" isConnectable={false} className="gwe-decision-node__handle" />
      <Handle type="source" position={Position.Left} id="left" isConnectable={false} className="gwe-decision-node__handle gwe-decision-node__handle--side" />
      <Handle type="source" position={Position.Right} id="right" isConnectable={false} className="gwe-decision-node__handle gwe-decision-node__handle--side" />
    </>
  );
  
  return (
    <div
      className={['gwe-decision-node', runningClass(activeRuns)].filter(Boolean).join(' ')}
      onClick={(e) => onClick?.(e)}
      onContextMenu={handleContextMenu}
    >
      {handles}
      
      {canDelete && (
        <button
          className="gwe-decision-node__remove-btn"
          onClick={(e) => { e.stopPropagation(); actions?.onRequestRemoveNode(nodeId); }}
          title="Delete branch"
        >
          ×
        </button>
      )}
      
      <div className="gwe-decision-node__card">
        <div className="gwe-decision-node__icon-box gwe-run-stack-host">
          <RunStack activeRuns={activeRuns} />
          <IonIcon icon={gitBranchOutline} className="gwe-decision-node__icon" />
        </div>
        <div className="gwe-decision-node__label">{label}</div>
        {description && (
          <div className="gwe-node-caption">
            <div className="gwe-decision-node__description">{description}</div>
            <div className="gwe-node-caption__tip" role="tooltip">
              {descriptionFull || description}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
